// types
import { ReviewedCart } from "@cart-client/interfaces/Cart";
import {
    BOOLEAN_AS_STRING,
    DELIVERY_SPEED,
    DELIVERY_SPEED_STRATEGY,
    IBenefitReinforcementComms,
    UserAddress,
} from "@swiggy-private/cart-types";

// util
import { isEmpty } from "@swiggy-private/js-utils";
import { isBlackMembershipPlan } from "./CheckBlackMembershipPlan";

import { hasSelectedAddress } from "./CartFooter";
import { getRestaurantId, getSessionId } from "./Cart";
import { BOLT_CART_HEADER_LOCAL_STORAGE_KEY, CART_HEADER_LOCAL_STORAGE_KEY } from "@cart-client/constants/cartHeader";
import {
    BoltUpdateOrInitializeFrequencyCappingParams,
    IProcessCartData,
    IUpdateCartHeaderState,
    UpdateOrInitializeFrequencyCappingParams,
} from "@cart-client/interfaces/CartHeaderLottie";
import { getSelectedSLA } from "./Header";
import { LottieCartType } from "@cart-client/hooks/useCommonCartheaderLottie/constants";
import { shouldSkipAnimationForMagicItems } from "@cart-client/hooks/useCommonCartheaderLottie/helpers";

const EmptyProcessCartData: IProcessCartData = {
    isBlck: false,
    isOtg: false,
    isExpress: false,
    rxId: "",
    cartHeaderSessionObject: {},
    sessionFreqCapKey: "",
    sid: "",
    frequencyData: undefined,
    isBolt: false,
    cartLocalStorageKey: "",
    boltUpdateKey: "",
    skipAnimationShowFinalState: false,
};

export const processCartData = (reviewedCart: ReviewedCart | null): IProcessCartData => {
    if (isEmpty(reviewedCart)) {
        return EmptyProcessCartData;
    }
    const isAddressSelected = hasSelectedAddress(reviewedCart.data?.addresses);

    if (!isAddressSelected) {
        return EmptyProcessCartData;
    }

    const isBlck = isBlackMembershipPlan(reviewedCart) || isBlackAddedToCart(reviewedCart);
    const isOtg = isOTGEnabled(reviewedCart) ?? false;
    const isExpress = isExpressEnabled(reviewedCart) ?? false;
    const isBolt = isBoltV3Enabled(reviewedCart) ?? false;
    const rxId = getRestaurantId(reviewedCart);
    const sid = getSessionId();
    const userAddress = reviewedCart?.data?.addresses?.find(
        (address: UserAddress) => address.id === reviewedCart.data?.address_id,
    );
    const boltSla =
        isBolt && userAddress?.sla_options
            ? getSelectedSLA(userAddress.sla_options, userAddress.selected_sla_option || "")
            : null;
    const sessionFreqCapKey = `${rxId}-${sid}`; // for freqCapping on each Restaurant for one session
    const cartLocalStorageKey = isBolt ? BOLT_CART_HEADER_LOCAL_STORAGE_KEY : CART_HEADER_LOCAL_STORAGE_KEY;
    const cartHeaderSession = localStorage.getItem(cartLocalStorageKey);
    const cartHeaderSessionObject = cartHeaderSession ? JSON.parse(cartHeaderSession) : {};
    const frequencyData = !isBolt ? getFrequencyCappingEnabledData(reviewedCart) : undefined;
    const boltUpdateKey = isBolt ? `${rxId}${boltSla ?? ""}` : "";
    const skipAnimationShowFinalState = shouldSkipAnimationForMagicItems(reviewedCart, isBolt, isBlck, sid);

    return {
        isBlck,
        isOtg,
        isExpress,
        rxId,
        cartHeaderSessionObject,
        sessionFreqCapKey,
        sid,
        frequencyData,
        isBolt,
        cartLocalStorageKey,
        boltUpdateKey,
        skipAnimationShowFinalState,
    };
};

export const getFrequencyCappingEnabledData = (
    cartResponse: ReviewedCart | null,
): IBenefitReinforcementComms | undefined => {
    if (isEmpty(cartResponse)) {
        return;
    }
    if (!isEmpty(cartResponse)) {
        const benefitReinforcementComms =
            cartResponse?.data?.swiggyOneInfo?.presentationInfo?.benefit_reinforcement_comms;
        if (!isEmpty(benefitReinforcementComms) && !isEmpty(benefitReinforcementComms.frequency_cap.id)) {
            return benefitReinforcementComms;
        }
    }
    return;
};

export const isOTGEnabled = (cartResponse: ReviewedCart | null) => {
    if (!isEmpty(cartResponse)) {
        return cartResponse.data?.is_swiggy_black_otp_applicable || false;
    }
};

export const isBlackAddedToCart = (cartResponse: ReviewedCart | null) => {
    if (isEmpty(cartResponse)) {
        return false;
    }
    if (!isEmpty(cartResponse)) {
        const subscriptionItems = cartResponse.data?.subscription_items || [];
        if (subscriptionItems.length >= 1 && isBlackMembershipPlan(cartResponse)) {
            return true;
        }
    }
    return false;
};

export const isExpressEnabled = (cartResponse: ReviewedCart | null): boolean => {
    if (isEmpty(cartResponse)) {
        return false;
    }
    if (!isEmpty(cartResponse)) {
        const addresses: UserAddress[] = cartResponse.data?.addresses || [];
        for (const address of addresses) {
            if (address && address.selected) {
                const expressOption = address.sla_options?.find((option) => option.type === DELIVERY_SPEED.EXPRESS);
                if (expressOption && expressOption.strategy === DELIVERY_SPEED_STRATEGY.VISIBLE) {
                    return true;
                }
            }
        }
    }
    return false;
};

export const isBoltV3Enabled = (cartResponse: ReviewedCart | null): boolean => {
    if (isEmpty(cartResponse)) {
        return false;
    }
    if (
        cartResponse.data?.is_bolt_experience_enabled_for_bolt_cart &&
        cartResponse.data?.XPData?.bolt_3_0_xp === BOOLEAN_AS_STRING.TRUE
    ) {
        return true;
    }
    return false;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isValuesEqual = (value: any, other: any): boolean => {
    if (value === other) {
        return true;
    }

    if (value == null || other == null || typeof value !== "object" || typeof other !== "object") {
        return false;
    }

    const valueKeys = Object.keys(value);
    const otherKeys = Object.keys(other);

    if (valueKeys.length !== otherKeys.length) {
        return false;
    }
    for (const key of valueKeys) {
        if (!otherKeys.includes(key) || !isValuesEqual(value[key], other[key])) {
            return false;
        }
    }

    return true;
};

const isFrequencyCapReached = (sessionObject: Record<string, any>, capId: string, maxCount: number): boolean =>
    sessionObject[capId] >= maxCount;

export const isCartHeaderTypeLottie = (cartType: string): boolean => {
    return cartType === LottieCartType._99STORE || cartType === LottieCartType.EO_CART;
};

const updateFrequencyCapping = ({
    cartHeaderSessionObject = {},
    frequencyCapId,
    sessionFreqCapKey,
    sid,
    cartLocalStorageKey,
}: UpdateOrInitializeFrequencyCappingParams) => {
    const newSessionObject = {
        ...cartHeaderSessionObject,
        [frequencyCapId]: (cartHeaderSessionObject[frequencyCapId] || 0) + 1,
        [sessionFreqCapKey]: 1,
        [sid]: (cartHeaderSessionObject[sid] || 0) + 1,
    };
    localStorage.setItem(cartLocalStorageKey, JSON.stringify(newSessionObject));
};

const updateBoltFrequencyCapping = ({
    cartHeaderSessionObject = {},
    sessionFreqCapKey,
    cartLocalStorageKey,
    boltUpdateKey,
}: BoltUpdateOrInitializeFrequencyCappingParams) => {
    let newSessionObject = {};
    if (cartHeaderSessionObject["id"] === sessionFreqCapKey) {
        newSessionObject = {
            ...cartHeaderSessionObject,
            [sessionFreqCapKey]: 1,
            rxSla: boltUpdateKey,
        };
    } else {
        newSessionObject = {
            id: sessionFreqCapKey,
            [sessionFreqCapKey]: 1,
            rxSla: boltUpdateKey,
        };
    }
    localStorage.setItem(cartLocalStorageKey, JSON.stringify(newSessionObject));
};

export const updateCartHeaderState = ({
    cartHeaderSessionObject = {},
    frequencyCapId,
    count,
    rxId,
    sessionFreqCapKey,
    sessionCount,
    sid,
    INITIAL_CART_HEADER_CONTEXT_VALUE,
    cartLocalStorageKey,
    sendAnalytics,
    newCartHeaderState,
    isBolt,
    boltUpdateKey = "",
}: IUpdateCartHeaderState) => {
    let shouldHideTheme = false;
    const hasFrequencyCapData = frequencyCapId && cartHeaderSessionObject[frequencyCapId];
    const isRxIdNotEmpty = !isEmpty(rxId);
    const isSessionShown = cartHeaderSessionObject[sessionFreqCapKey] === 1;
    const isSessionCapCrossed = sessionCount && (cartHeaderSessionObject[sid] || 0) >= sessionCount;
    let finalCartHeaderState = { ...newCartHeaderState };

    if (isBolt) {
        if (isRxIdNotEmpty && isSessionShown) {
            // if shown for restaurant once per session
            finalCartHeaderState = {
                ...finalCartHeaderState,
                showFinalState: true,
                themeChange: true,
                hideTheme: false,
            };
        } else {
            sendAnalytics();
            updateBoltFrequencyCapping({
                cartHeaderSessionObject,
                cartLocalStorageKey,
                sessionFreqCapKey,
                boltUpdateKey,
            });
        }
    } else {
        if (hasFrequencyCapData) {
            // Case 1: Frequency cap not yet reached
            if (
                isRxIdNotEmpty &&
                count &&
                frequencyCapId &&
                !isFrequencyCapReached(cartHeaderSessionObject, frequencyCapId, count)
            ) {
                // Sub-case: Current session already shown
                if (isSessionShown) {
                    finalCartHeaderState = {
                        ...finalCartHeaderState,
                        showFinalState: true,
                        themeChange: true,
                        hideTheme: false,
                    };
                } else if (!isSessionCapCrossed) {
                    // Sub-case: Frequency cap for new session
                    sendAnalytics();
                    updateFrequencyCapping({
                        cartHeaderSessionObject,
                        frequencyCapId,
                        sessionFreqCapKey,
                        sid,
                        cartLocalStorageKey,
                    });
                } else {
                    // Sub-case: Session frequency cap crossed
                    shouldHideTheme = true;
                }
            } else if (
                count &&
                frequencyCapId &&
                isFrequencyCapReached(cartHeaderSessionObject, frequencyCapId, count)
            ) {
                // Case 2: Frequency cap limit is reached
                if (!isSessionShown) {
                    shouldHideTheme = true;
                }
            }
        } else if (count && frequencyCapId && sessionCount && count > 0 && sessionCount > 0) {
            // Case 3: Initialize frequency capping if not present
            sendAnalytics();
            updateFrequencyCapping({
                cartHeaderSessionObject: {},
                frequencyCapId,
                sessionFreqCapKey,
                sid,
                cartLocalStorageKey,
            });
        }
    }
    if (shouldHideTheme) {
        return {
            newCartHeaderState: {
                ...INITIAL_CART_HEADER_CONTEXT_VALUE.cartHeaderState,
                hideTheme: true,
            },
            shouldHideTheme,
        };
    }
    // Return the updated state and the flag indicating whether to hide the theme
    return { newCartHeaderState: { ...finalCartHeaderState, hideTheme: false }, shouldHideTheme };
};
